// Fetches a DQ by id. This is what lets <DQDisplay> take a DQ id and nothing
// else: it fetches through this hook, renders, and exposes interaction.
//
// Supports the three display modes:
//   test          live question-answering, no correctness in the response
//   review        post-submission, with correctness and misconception_type
//   display-only  tutor preview and reports, read-only with no correctness

import { useEffect, useState } from "react"
import { getDQ } from "../../modules/diagnostics/services/bmd.service"
import type { DQ, DQDisplayMode } from "../../modules/diagnostics/types/bmd.types"

export type UseDQState = {
  dq: DQ | null
  loading: boolean
  error: string | null
  refetch: () => void
}

export function useDQ(
  dqId: number,
  mode: DQDisplayMode = "test",
  sessionQuestionKey?: string,
): UseDQState {
  const [dq, setDq] = useState<DQ | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [refetchCounter, setRefetchCounter] = useState<number>(0)

  useEffect(() => {
    if (!dqId) {
      setDq(null)
      setLoading(false)
      setError(null)
      return
    }

    const controller = new AbortController()
    setLoading(true)
    setError(null)
    // Clear the previous DQ immediately so DQDisplay unmounts the old
    // <MathJax> tree cleanly before the new question arrives. Without this,
    // better-react-mathjax's async typeset can fire against a torn-down
    // DOM and throw "Cannot read properties of null (reading 'nextSibling')".
    setDq(null)

    getDQ(
      {
        diagnostic_id: dqId,
        mode,
        pk_session_question_key: sessionQuestionKey,
      },
      controller.signal,
    )
      .then((data) => {
        setDq(data)
        setLoading(false)
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        setError(err?.message || "Failed to load question")
        setDq(null)
        setLoading(false)
      })

    return () => {
      controller.abort()
    }
  }, [dqId, mode, sessionQuestionKey, refetchCounter])

  const refetch = () => setRefetchCounter((prev) => prev + 1)

  return { dq, loading, error, refetch }
}
